"use client";

import { Message } from "@/lib/core/crm/types";
import { Client } from "@/lib/types/database";
import { ModalContainer, MODAL_IDS } from "@west-tennessee-consulting/ui";
import { ComposeModalWrapper, ComposeMessageFormValues } from "./ComposeModalWrapper";
import { VariableProvider } from "../contexts/VariableContext";
import * as React from "react";
import { useComposeMessageSend } from "@/lib/hooks/compose/use-compose-message-send";
import { EmailRecipient } from "../ClientEmailSelector";
import { useReplyContext } from "../contexts/ReplyContext";
import { useComposeUrlParams } from "@/lib/hooks/compose/useComposeUrlParams";
import { useComposeMessageInit } from "@/lib/hooks/compose/useComposeMessageInit";
import { getSupabase } from "@/lib/hooks/api/utils";
import type { ComposeMessageModalData } from "@/lib/modals/data/modal-data-types";

export type { ComposeMessageFormValues };

export function ComposeModalContainer() {
  return (
    <ModalContainer<ComposeMessageModalData> modalId={MODAL_IDS.messages.compose}>
      {({ isOpen, data, onClose }) => (
        <ComposeModalContainerContent
          isOpen={isOpen}
          data={data}
          onClose={onClose}
        />
      )}
    </ModalContainer>
  );
}

interface ComposeModalContainerContentProps {
  isOpen: boolean;
  data?: ComposeMessageModalData;
  onClose: () => void;
}

function ComposeModalContainerContent({
  isOpen,
  data,
  onClose,
}: ComposeModalContainerContentProps) {
  const { replyingToMessage, setReplyingToMessage } = useReplyContext();

  // Use new URL params hook
  const urlParams = useComposeUrlParams({ isOpen });

  // Fetch contact data from URL when modal opens
  React.useEffect(() => {
    const fetchContactDataFromUrl = async () => {
      if (!isOpen) {
        urlParams.resetUrlContactData();
        return;
      }

      const contactsId = urlParams.extractContactIdFromUrl();

      if (!contactsId) {
        urlParams.resetUrlContactData();
        return;
      }

      if (data?.customerEmail) {
        urlParams.resetUrlContactData();
        return;
      }

      try {
        const supabase = getSupabase();
        const { data: contact, error } = await supabase
          .from("contacts")
          .select("id, name, email")
          .eq("id", contactsId)
          .single();

        if (error) {
          console.error("Failed to fetch contact for compose modal:", error);
          urlParams.resetUrlContactData();
          return;
        }

        if (contact) {
          urlParams.setContactData({
            email: contact.email,
            name: contact.name,
          });
        }
      } catch (error) {
        console.error("Error fetching contact data:", error);
        urlParams.resetUrlContactData();
      }
    };

    void fetchContactDataFromUrl();
  }, [isOpen, urlParams, data?.customerEmail]);

  // Determine modes
  const isReplyMode = data?.mode === "reply" || !!replyingToMessage;
  const isForwardMode = !isReplyMode && data?.mode === "forward";

  const forwardingMessage =
    isForwardMode && data?.message ? (data.message as Message) : null;

  const replyMessage =
    replyingToMessage ||
    (isReplyMode && data?.message ? (data.message as Message) : null);
  const originalMessage = replyMessage || forwardingMessage;

  // Use message initialization hook
  const messageInit = useComposeMessageInit({
    isReplyMode,
    isForwardMode,
    replyMessage,
    replyingToMessage,
    forwardingMessage,
    modalData: data,
    urlContactData: urlParams.urlContactData,
    propInitialRecipient: undefined,
    propInitialClient: undefined,
    propInitialRecipients: undefined,
    propInitialSubject: undefined,
    propInitialBody: undefined,
  });

  const [messageSent, setMessageSent] = React.useState(false);

  const { handleSendMessage, isSending } = useComposeMessageSend(
    () => {
      setMessageSent(true);
    },
    isForwardMode ? originalMessage : null,
    isForwardMode
  );

  const handleMessageSent = React.useCallback(() => {
    // follow-up handled later by Wrapper when we re-enable it
  }, []);

  const handleFollowUpTaskModalClose = React.useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
        setMessageSent(false);
        setReplyingToMessage(null);
      }
    },
    [onClose, setReplyingToMessage]
  );

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      // prevent echo loops with global modal stack
      if (open === isOpen) return;

      if (!open) {
        onClose();
        setReplyingToMessage(null);
      }
    },
    [isOpen, onClose, setReplyingToMessage]
  );

  return (
    <VariableProvider>
      {isOpen && (
        <ComposeModalWrapper
          key={`${data?.mode || (isForwardMode ? "forward" : isReplyMode ? "reply" : "compose")}-${messageInit.originalMessage?.id || "new"}`}
          isOpen={isOpen}
          onOpenChange={handleOpenChange}
          originalMessage={messageInit.originalMessage}
          onSubmit={handleSendMessage}
          isSending={isSending}
          initialClient={messageInit.initialClientForForm}
          initialRecipient={messageInit.replyEmail}
          initialRecipients={messageInit.initialRecipientsForForm}
          initialBccRecipients={messageInit.initialBccRecipientsForForm}
          initialSubject={messageInit.initialSubjectForForm}
          initialBody={messageInit.initialBodyForForm}
          onMessageSent={messageSent ? handleMessageSent : undefined}
          onFollowUpTaskModalClose={handleFollowUpTaskModalClose}
          globalModalId={MODAL_IDS.messages.compose}
          isForwardMode={isForwardMode}
        />
      )}
    </VariableProvider>
  );
}
